/**
 * Cross-source email resolution utilities.
 *
 * Shared by customer merge (Stripe ↔ Linear ↔ Datadog) and
 * contributor identity resolution (GitHub emails ↔ logins).
 * Extracted from merge-customers.ts so the same logic is reusable
 * across any cross-source join.
 */

// ---------------------------------------------------------------------------
// Email normalisation
// ---------------------------------------------------------------------------

export function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function extractDomain(email: string): string {
  const parts = email.split('@');
  return parts[1] ?? '';
}

// cspell:ignore googlemail
const GMAIL_DOMAINS = new Set(['gmail.com', 'googlemail.com']);

// cspell:ignore canonicalise
/**
 * Canonicalise an email for cross-source matching.
 *
 * Goes beyond normaliseEmail() (which only trims + lowercases) to handle
 * provider-specific aliasing that causes the same person to appear as
 * different emails across Stripe, Datadog, Linear, etc.
 *
 * Gmail/Google rules applied:
 * 1. Strip `+suffix` from the local part (james+work@gmail.com → james@gmail.com)
 * 2. Remove dots from the local part (j.ames@gmail.com → james@gmail.com)
 * 3. Normalise googlemail.com → gmail.com
 */
export function canonicaliseEmail(email: string): string {
  const normalised = normaliseEmail(email);
  const atIndex = normalised.indexOf('@');
  if (atIndex === -1) return normalised;

  let local = normalised.slice(0, atIndex);
  let domain = normalised.slice(atIndex + 1);

  if (GMAIL_DOMAINS.has(domain)) {
    // Strip +suffix
    const plusIndex = local.indexOf('+');
    if (plusIndex !== -1) {
      local = local.slice(0, plusIndex);
    }
    // Remove dots from local part
    local = local.replaceAll('.', '');
    // Normalise domain
    domain = 'gmail.com';
  }

  return `${local}@${domain}`;
}

// ---------------------------------------------------------------------------
// Freemail detection (prevents false domain matches)
// ---------------------------------------------------------------------------

// cspell:ignore ymail tutanota
const FREEMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'mail.com',
  'protonmail.com',
  'proton.me',
  'live.com',
  'msn.com',
  'ymail.com',
  'me.com',
  'hey.com',
  'fastmail.com',
  'tutanota.com',
]);

export function isFreemailDomain(domain: string): boolean {
  return FREEMAIL_DOMAINS.has(domain.toLowerCase());
}

// ---------------------------------------------------------------------------
// Cross-source email index
// ---------------------------------------------------------------------------

export type IdentitySource = 'github' | 'stripe' | 'linear' | 'datadog';

export interface EmailIdentity {
  readonly canonicalEmail: string;
  readonly sources: ReadonlySet<IdentitySource>;
  readonly gitHubLogin: string | null;
  readonly displayName: string | null;
}

/**
 * Build a cross-source email index from emails collected across all
 * integrations. Keyed by canonical email (Gmail alias handling).
 */
export function buildCrossSourceEmailIndex(
  entries: ReadonlyArray<{
    readonly email: string;
    readonly source: IdentitySource;
    readonly gitHubLogin?: string;
    readonly displayName?: string;
  }>,
): Map<string, EmailIdentity> {
  const index = new Map<
    string,
    {
      canonicalEmail: string;
      sources: Set<IdentitySource>;
      gitHubLogin: string | null;
      displayName: string | null;
    }
  >();

  for (const entry of entries) {
    const key = canonicaliseEmail(entry.email);
    if (key.length === 0) continue;

    const existing = index.get(key);
    if (existing !== undefined) {
      existing.sources.add(entry.source);
      if (entry.gitHubLogin !== undefined && existing.gitHubLogin === null) {
        existing.gitHubLogin = entry.gitHubLogin;
      }
      if (entry.displayName !== undefined && existing.displayName === null) {
        existing.displayName = entry.displayName;
      }
    } else {
      index.set(key, {
        canonicalEmail: key,
        sources: new Set([entry.source]),
        gitHubLogin: entry.gitHubLogin ?? null,
        displayName: entry.displayName ?? null,
      });
    }
  }

  return index;
}
